import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AdminPromoCodes() {
    const [codes, setCodes] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => { const { data } = await api.get("/admin/promo-codes"); setCodes(data); };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing({ code: "", discount_type: "percent", value: 10, active: true, max_uses: null, expires_at: null }); setOpen(true); };
    const openEdit = (c) => { setEditing({ ...c }); setOpen(true); };

    const save = async () => {
        try {
            const payload = { ...editing };
            if (editing.id) await api.put(`/admin/promo-codes/${editing.id}`, payload);
            else await api.post("/admin/promo-codes", payload);
            toast.success("Guardado");
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (id) => { if (!window.confirm("Eliminar?")) return; await api.delete(`/admin/promo-codes/${id}`); load(); };

    return (
        <div className="space-y-6" data-testid="admin-promo">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">Códigos promocionais</h1>
                    <p className="text-white/60">Descontos percentuais ou fixos.</p>
                </div>
                <Button onClick={openNew} className="ab-btn-primary rounded-full" data-testid="add-promo"><Plus className="w-4 h-4 mr-2" /> Novo código</Button>
            </div>

            <div className="ab-card overflow-hidden">
                <table className="w-full">
                    <thead className="bg-[#171717] border-b border-[#2A2A2A] text-xs uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left p-4">Código</th>
                            <th className="text-left p-4">Tipo</th>
                            <th className="text-right p-4">Valor</th>
                            <th className="text-center p-4">Ativo</th>
                            <th className="text-right p-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2A2A2A]">
                        {codes.length === 0 ? <tr><td colSpan={5} className="text-center p-10 text-white/50">Sem códigos.</td></tr> :
                            codes.map((c) => (
                                <tr key={c.id}>
                                    <td className="p-4 font-mono font-bold">{c.code}</td>
                                    <td className="p-4">{c.discount_type === "percent" ? "Percentagem" : "Fixo"}</td>
                                    <td className="p-4 text-right font-heading font-bold">{c.value}{c.discount_type === "percent" ? " %" : " €"}</td>
                                    <td className="p-4 text-center">{c.active ? "✓" : "—"}</td>
                                    <td className="p-4 text-right">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)}><Edit3 className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => remove(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing?.id ? "Editar código" : "Novo código"}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div>
                                <Label>Código</Label>
                                <Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Tipo</Label>
                                    <Select value={editing.discount_type} onValueChange={(v) => setEditing({ ...editing, discount_type: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="percent">Percentagem</SelectItem>
                                            <SelectItem value="fixed">Valor fixo (€)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Valor</Label>
                                    <Input type="number" step="0.5" value={editing.value} onChange={(e) => setEditing({ ...editing, value: Number(e.target.value) })} />
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /> <Label>Ativo</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} className="ab-btn-primary"><Save className="w-4 h-4 mr-2" />Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
